import { type SelectProps as AntSelectProps, Select as AntdSelect } from 'antd';
import type { BaseOptionType, DefaultOptionType } from 'antd/lib/select';
import type { VFC } from 'react';
import { Icon } from '@/components/icon';
import { removeAccents } from '@/utils/string/removeAccents';
import { Tag } from '@/components/tag/Tag';
import { FormattedMessage } from '../../formatted_message/FormattedMessage';
import { SelectMessages as messages } from './messages';

export type SelectProps<T, U extends BaseOptionType | DefaultOptionType = DefaultOptionType> = AntSelectProps<T, U>;

const tagRender: SelectProps<any, any>['tagRender'] = ({ label, value, ...props }) => {
  return <Tag {...props}>{label || value}</Tag>;
};

/**
 * Styled Ant Select
 * Automatically trims the search value before calling the `onSearch` prop.
 */
export function Select<T, U extends BaseOptionType | DefaultOptionType = DefaultOptionType>(
  props: SelectProps<T, U>,
): ReturnType<VFC<SelectProps<T, U>>> {
  const { suffixIcon, ...propsToPass } = props;

  const loadingProps = propsToPass.loading
    ? {
        value: undefined,
        disabled: true,
        placeholder: <FormattedMessage {...messages.loadingPlaceholder} />,
        suffixIcon: <Icon spin iconString="spinner" />,
      }
    : {};

  return (
    <AntdSelect
      filterOption={(input, option) => {
        const searched = removeAccents(input).toLowerCase().trim();

        if (typeof option?.label === 'string') return removeAccents(option.label).toLowerCase().includes(searched);
        return false;
      }}
      suffixIcon={suffixIcon ?? <Icon iconString="chevronDown" />}
      tagRender={tagRender}
      {...propsToPass}
      {...loadingProps}
    />
  );
}

Select.Option = AntdSelect.Option;
Select.OptGroup = AntdSelect.OptGroup;
